import { useState, useEffect } from 'react';

interface Review {
  id: string;
  author: string;
  rating: number;
  comment: string;
  date: string;
  role: string;
}

interface PlatformRatingModalProps {
  platformName: string;
  platformUrl: string;
  onClose: () => void;
}

const ROLES = ['投资者', '从业者', '研究员', '开发者', '律师/合规', '其他'];

const MOCK_REVIEWS: Record<string, Review[]> = {
  'RWA.xyz': [
    { id: '1', author: '张先生', rating: 5, comment: '数据最全面，TVL追踪非常准确，是我每天必看的平台。', date: '2026-03-15', role: '投资者' },
    { id: '2', author: 'Michael L.', rating: 5, comment: 'Best RWA data aggregator, real-time and comprehensive.', date: '2026-03-10', role: '研究员' },
    { id: '3', author: '李研究员', rating: 4, comment: '数据质量高，但部分新兴市场项目覆盖还不够全面。', date: '2026-02-28', role: '研究员' },
  ],
  'Securitize': [
    { id: '1', author: '王律师', rating: 5, comment: '合规体系最完善，双牌照背书让机构客户非常放心。', date: '2026-03-20', role: '律师/合规' },
    { id: '2', author: '陈总监', rating: 4, comment: '服务专业，但费用较高，适合大型机构。', date: '2026-03-05', role: '从业者' },
  ],
  'HashKey': [
    { id: '1', author: '林先生', rating: 5, comment: '香港本地最好的RWA平台，团队响应速度快。', date: '2026-03-18', role: '从业者' },
    { id: '2', author: '黄投资人', rating: 4, comment: '产品线丰富，一站式服务体验很好。', date: '2026-03-01', role: '投资者' },
  ],
};

function StarRating({ value, onChange, size = 'md' }: { value: number; onChange?: (v: number) => void; size?: 'sm' | 'md' | 'lg' }) {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-sm' : 'text-lg';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange?.(star)}
          onMouseEnter={() => onChange && setHover(star)}
          onMouseLeave={() => onChange && setHover(0)}
          className={`${sizeClass} transition-colors ${onChange ? 'cursor-pointer' : 'cursor-default'}`}
          style={{ color: (hover || value) >= star ? '#F59E0B' : '#D1D5DB' }}
        >
          <i className="ri-star-fill"></i>
        </button>
      ))}
    </div>
  );
}

export default function PlatformRatingModal({ platformName, platformUrl, onClose }: PlatformRatingModalProps) {
  const storageKey = `platform_reviews_${platformName}`;
  const [reviews, setReviews] = useState<Review[]>([]);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [myRole, setMyRole] = useState('');
  const [myName, setMyName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<'reviews' | 'write'>('reviews');

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    const base = MOCK_REVIEWS[platformName] ?? [];
    if (stored) {
      const userReviews: Review[] = JSON.parse(stored);
      setReviews([...userReviews, ...base]);
    } else {
      setReviews(base);
    }
  }, [platformName, storageKey]);

  const avgRating = reviews.length > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  const ratingDist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));

  const handleSubmit = () => {
    if (!myRating || !myComment.trim() || !myRole) return;
    const newReview: Review = {
      id: Date.now().toString(),
      author: myName.trim() || '匿名用户',
      rating: myRating,
      comment: myComment.trim(),
      date: new Date().toISOString().split('T')[0],
      role: myRole,
    };
    const stored = localStorage.getItem(storageKey);
    const existing: Review[] = stored ? JSON.parse(stored) : [];
    const updated = [newReview, ...existing];
    localStorage.setItem(storageKey, JSON.stringify(updated));
    const base = MOCK_REVIEWS[platformName] ?? [];
    setReviews([...updated, ...base]);
    setSubmitted(true);
    setActiveTab('reviews');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-black text-gray-900">{platformName}</h3>
              <p className="text-sm text-gray-400 mt-0.5">{platformUrl.replace('https://', '')}</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer shrink-0">
              <i className="ri-close-line text-gray-600"></i>
            </button>
          </div>

          {/* Rating Summary */}
          <div className="flex items-center gap-6 mt-4">
            <div className="text-center">
              <div className="text-4xl font-black text-gray-900">{avgRating > 0 ? avgRating.toFixed(1) : '--'}</div>
              <StarRating value={Math.round(avgRating)} size="sm" />
              <div className="text-xs text-gray-400 mt-1">{reviews.length} 条评价</div>
            </div>
            <div className="flex-1 space-y-1">
              {ratingDist.map(({ star, count }) => (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-3">{star}</span>
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: reviews.length > 0 ? `${(count / reviews.length) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400 w-4">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === 'reviews' ? 'bg-white text-gray-900' : 'text-gray-500'}`}
            >
              全部评价
            </button>
            <button
              onClick={() => setActiveTab('write')}
              className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${activeTab === 'write' ? 'bg-white text-gray-900' : 'text-gray-500'}`}
            >
              写评价
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'reviews' && (
            <div className="p-6 space-y-4">
              {reviews.length === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <i className="ri-chat-3-line text-3xl mb-2 block"></i>
                  <p className="text-sm">暂无评价，成为第一个评价者</p>
                </div>
              )}
              {reviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#7C3AED]/10 text-[#7C3AED] font-bold text-sm shrink-0">
                        {review.author[0]}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900">{review.author}</div>
                        <div className="text-xs text-gray-400">{review.role} · {review.date}</div>
                      </div>
                    </div>
                    <StarRating value={review.rating} size="sm" />
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'write' && (
            <div className="p-6">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-green-600 text-2xl"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">评价已提交！</h4>
                  <p className="text-sm text-gray-500 mb-4">感谢您的反馈，帮助更多用户了解这个平台</p>
                  <button
                    onClick={() => { setSubmitted(false); setMyRating(0); setMyComment(''); setMyRole(''); setMyName(''); setActiveTab('reviews'); }}
                    className="px-4 py-2 bg-[#7C3AED] text-white rounded-xl text-sm font-semibold cursor-pointer"
                  >
                    查看所有评价
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="text-sm font-bold text-gray-700 block mb-2">您的评分 <span className="text-red-500">*</span></label>
                    <StarRating value={myRating} onChange={setMyRating} size="lg" />
                    {myRating > 0 && (
                      <span className="text-sm text-amber-600 mt-1 block">
                        {['', '非常差', '较差', '一般', '不错', '非常好'][myRating]}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-700 block mb-2">您的身份 <span className="text-red-500">*</span></label>
                    <div className="flex flex-wrap gap-2">
                      {ROLES.map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setMyRole(role)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer border ${
                            myRole === role ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#7C3AED]/40'
                          }`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-700 block mb-2">您的评价 <span className="text-red-500">*</span></label>
                    <textarea
                      value={myComment}
                      onChange={(e) => setMyComment(e.target.value.slice(0, 300))}
                      placeholder="分享您使用这个平台的真实体验..."
                      className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:border-[#7C3AED]/50 h-28"
                    />
                    <div className="text-xs text-gray-400 text-right">{myComment.length}/300</div>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-gray-700 block mb-2">昵称（选填）</label>
                    <input
                      value={myName}
                      onChange={(e) => setMyName(e.target.value.slice(0, 20))}
                      placeholder="匿名用户"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]/50"
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={!myRating || !myComment.trim() || !myRole}
                    className="w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed text-white"
                    style={{ background: '#7C3AED' }}
                  >
                    提交评价
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
